import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { NgIf, NgFor, CommonModule } from '@angular/common';
import { LoadingComponent } from '../../components/loading/loading.component';
import { OfferCardComponent } from '../../components/offer-card/offer-card.component';
import { Bind } from 'primeng/bind';
import { Drawer, DrawerModule } from 'primeng/drawer';
import { RadioButton } from 'primeng/radiobutton';
import { FormsModule } from '@angular/forms';
import { Checkbox } from 'primeng/checkbox';
import { Button } from 'primeng/button';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { ApiService } from '../../_utils/api/api.service';
import { slugify } from '../../_utils/functions';
import { InsightService } from '../../_utils/insight/insight.service';
import { SeoService } from '../../_utils/seo/seo.service';

@Component({
    selector: 'app-offers',
    templateUrl: './offers.component.html',
    styleUrl: './offers.component.scss',
    imports: [CommonModule, LoadingComponent, TranslocoDirective, OfferCardComponent, Bind, DrawerModule, RadioButton, FormsModule, Checkbox, Button, TranslatePipe]
})
export class OffersComponent implements OnInit {
  loading = signal(false);
  error: string = "";
  filterOptions: any = { type: 'all', tags: null, orderBy: 'recent'};
  offers: any[] = [];
  data: any = null;
  sidebarVisible: boolean = false;
  model: any = { category: 'all', tags: [], orderBy: 'recent'};
  // cached tags per category (array of {label,value,count}) to avoid heavy recomputation
  tagsByCategory: { [key: string]: any[] } = {};

  constructor(private translocoService: TranslocoService, private insightService: InsightService, private seoService: SeoService, private router: Router, private route: ActivatedRoute, private apiService: ApiService) { }

  /**
   * Check if category is contained in offers
   * @param category category name
   * @returns a boolean value indicating whether the category is contained in the list of offers
   */
  offersHave(category: string): boolean {
    return this.data.offers.filter((offer: any) => offer.tipologia == category).length > 0;
  }

  applyFilter(): void {
    this.filterByCategory(this.model.category);
    this.filterByTags(this.model.tags, true);
    this.orderBy(this.model.orderBy);
    this.sidebarVisible = false;
  }

  /**
   * Filter the list of offers by category
   * @param type category type
   */
  filterByCategory(type: any): void {
    this.filterOptions.type = type;
    if (type== 'all')
      this.offers = this.data.offers;
    else
      this.offers = this.data.offers.filter((offer: any) => offer.tipologia == type);
  }

  filterByTags(tags: any[], useOffers: boolean = false): void {
    console.log(tags);
    this.filterOptions.tags = tags;
    const LANG = this.translocoService.getActiveLang();
    // normalize incoming tags (accept array of strings or array of {value})
    const selectedTags = (tags || []).map((t: any) => typeof t === 'string' ? t : (t && t.value ? t.value : '')).filter((t: string) => t !== '').map(t => t.toLowerCase());

    if (selectedTags.length === 0) {
      if (!useOffers)
        this.offers = this.data.offers;
    } else {
      const offers = useOffers ? this.offers : this.data.offers;
      this.offers = offers.filter((offer: any) => {
      if (!offer.tags || !offer.tags[LANG] || offer.tags[LANG].trim().length === 0)
        return false;
      const offerTags = offer.tags[LANG].split(',').map((tt: string) => tt.trim().toLowerCase());
        return selectedTags.some(st => offerTags.indexOf(st) >= 0);
      });
    }
  }

  orderBy(type: any): void {
    this.filterOptions.orderBy = type;
    if (type == 'recent')
      this.offers = this.offers.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
    else if (type == 'less_recent')
      this.offers = this.offers.sort((a, b) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime());
    else if (type == 'expiration') {
      let parseDate = (date: string) => {
        if (!date)
          return new Date("2020-02-10T12:03:54.000000Z");
        let parts = date.split(" ");
        let dateParts = parts[0].split("-");
        let timeParts = parts[1].split(":");
        return new Date(parseInt(dateParts[2], 10), parseInt(dateParts[1], 10) - 1, parseInt(dateParts[0], 10), parseInt(timeParts[0], 10), parseInt(timeParts[1], 10));
      }
      this.offers = this.offers.sort((a, b) => parseDate(b.scadenza).getTime() - parseDate(a.scadenza).getTime());
    }
  }

  generateOfferUrl(offer: any): string {
    const translatePipe = new TranslatePipe(this.translocoService);
    return this.data.vanity_url ? slugify(translatePipe.transform(offer.titolo)) : offer.id;
  }

  ngOnInit(): void {
    this.loading.set(true);
    this.apiService.getCompany().subscribe({
      next: (data) => {
        this.loading.set(false);

        const translatePipe = new TranslatePipe(this.translocoService);
        let description = data.data.description ? translatePipe.transform(data.data.description) : null;
        this.seoService.setSeo({
          title: data.data.denominazione,
          description: description || data.data.denominazione,
          image: data.data.image || data.data.logo,
          url: data.data.pwa_url
        });

        if (typeof window === 'undefined') //skip for SSR
          return;

        this.error = "";
        this.data = data.data;
        this.offers = data.data.offers;

        const initialTags = this.readTagsQueryParam();
        if (initialTags.length > 0) {
          this.model.tags = initialTags;
          this.applyFilter();
        }

        // build tags once and cache per category to avoid expensive calls from the template
        this.buildTags();
        this.insightService.send({
          event: 'open_page',
          event_name: 'proposte'
        });
      },
      error: (error) => {
        console.error(error);
        this.router.navigate(['error'], { queryParams: { message: error } });
      }
    });
  }

  private readTagsQueryParam(): string[] {
    const rawTags = this.route.snapshot.queryParamMap.get('tags');
    if (!rawTags)
      return [];

    return rawTags
      .split(',')
      .map((tag: string) => {
        const normalizedTag = tag.trim();
        if (!normalizedTag)
          return '';

        try {
          return decodeURIComponent(normalizedTag);
        } catch {
          return normalizedTag;
        }
      })
      .filter((tag: string) => tag.length > 0);
  }

  /**
   * Return cached tags for a category, computed once after data load.
   */
  protected getTags(category: string): any[] {
    return this.tagsByCategory[category] || [];
  }

  /**
   * Build tagsByCategory map: for each category an array of {label, value, count}
   */
  private buildTags(): void {
    this.tagsByCategory = {};
    if (!this.data || !this.data.offers)
      return;
    const LANG = this.translocoService.getActiveLang();
    // temporary map to count occurrences per category
    const tmp: { [cat: string]: { [tag: string]: any } } = {};
    this.data.offers.forEach((offer: any) => {
      const cat = offer.tipologia || 'all';
      if (!tmp[cat]) tmp[cat] = {};
      if (offer.tags && offer.tags[LANG] && offer.tags[LANG].trim().length > 0) {
        const parts = offer.tags[LANG].split(',').map((tag: string) => tag.trim().toLowerCase()).filter((t: string) => t.length > 0);
        parts.forEach((tag: string) => {
          if (!tmp[cat][tag]) {
            tmp[cat][tag] = { label: tag ? tag.charAt(0).toUpperCase() + tag.slice(1) : tag, value: tag, count: 0 };
          }
          tmp[cat][tag].count++;
        });
      }
    });
    // convert to arrays
    Object.keys(tmp).forEach(cat => {
      this.tagsByCategory[cat] = Object.keys(tmp[cat]).map(k => tmp[cat][k]);
    });
  }

  clearFilters(): void {
    this.model = { category: 'all', tags: [], orderBy: 'recent'};
    this.applyFilter();
  }
}
